import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, filter, take } from 'rxjs';
import { appConstants } from '../../../../../shared/constants/app-constants';
import { Country } from '../../../../../shared/constants/srs-ballot.constants';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { IOption } from '../../../../controls/models/option';
import { ITableColumn, TableColumnType } from '../../../../controls/table/table-column';
import { ISdBallotDetailDto } from '../../../shared/models/sd/sd-ballot-detail';
import { ISdVoteDto } from '../../../shared/models/sd/sd-vote';
import { SdBallotService } from '../../../shared/services/sd-ballot.service';
import { SdHistoryColumnComponent } from './sd-history-column/sd-history-column.component';
import { SdVoteColumnComponent } from './sd-vote-column/sd-vote-column.component';

@Component({
    selector: 'app-sd-votes',
    templateUrl: './sd-votes.component.html',
    styleUrl: './sd-votes.component.scss',
    standalone: false
})
export class SdVotesComponent implements OnInit, OnDestroy {
  @Input() sdBallotDetail: ISdBallotDetailDto | null = null;
  @Output() afterSaveVote = new EventEmitter<string>();
  afterSaveVoteSubject: Subject<string> = new Subject<string>();

  ballotVotes: ISdVoteDto[] = [];
  allVotes: ISdVoteDto[] = [];
  showingVotes: ISdVoteDto[] = [];
  selectedVotes: ISdVoteDto[] = [];
  votingRightDropdown: string = '';
  locationDropdown: string = '';
  isShowingLocations: boolean = true;
  isJoint: boolean = false;
  selectedVoteCount: number = 0;
  isUserPmOrAbove: boolean = false;
  isUserCM: boolean = false;
  isHoSD: boolean = false;
  isUserPmOrAboveOrHoSD: boolean = false;
  currentUser: IUser | null = null;
  currentUserCommitteeCodes: string[] = [];
  subscriptions: Subscription = new Subscription();
  loginUserEmail: string = '';
  isDropdownChanged: boolean = false;
  selectedPageNumber: number = 1;
  pageSize: number = 50;
  totalRecords: number = 0;
  eligible = appConstants.votingRights.eligible;
  notEligible = appConstants.votingRights.notEligible;

  votingRightOptions: IOption<string>[] = [
    { value: 'eligible', label: this.eligible },
    { value: 'notEligible', label: this.notEligible },
  ];

  locationOptions: IOption<string>[] = [
    { value: 'aus-nz', label: 'AUS & NZ' },
    { value: 'au', label: 'AUS' },
    { value: 'nz', label: 'NZ' },
  ];



  constructor(
    public _authService: AuthService,
    private _samlService: SamlService,
    private _sdBallotService: SdBallotService,
  ) {
    this.initUser();
    this.setupAfterSaveSubjectSubscription();
  }

  initUser() {
    const currentUserSubscription =
      this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        if (this.currentUser) {
          this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
          this.isUserCM = UserUtility.isCommitteeMember(this.currentUser);
          this.loginUserEmail = this.currentUser.email;
          if(!this.isUserPmOrAbove) {
            this._samlService
            .getLoggedInUserHoSDStatus()
            .subscribe((response) => {
              this.isHoSD = response.content;
              this.isUserPmOrAboveOrHoSD = this.isUserPmOrAbove || this.isHoSD;
            });
          }else {
            this.isUserPmOrAboveOrHoSD = true
          }
          this.updateTableColumns();
        }
      });
    this.subscriptions.add(currentUserSubscription);
  }

  setupAfterSaveSubjectSubscription() {
    this.subscriptions.add(this.afterSaveVoteSubject.subscribe((response) => {
      if(response) {
        this.afterSaveVote.emit(response);
      }
    }));
  }


  viewVoteTableColumns: ITableColumn<ISdVoteDto>[] = [
    {
      name: 'committeeMember',
      displayName: 'Committee Member',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'organisation',
      displayName: 'Organisation',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'role',
      displayName: 'Role',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'country',
      displayName: 'Country',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'votingRights',
      displayName: 'Voting Rights',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'vote',
      displayName: 'Vote',
      type: TableColumnType.component,
      component: SdVoteColumnComponent,
      getComponentInput: (row: ISdVoteDto) => {
        return {
          vote: row,
          isUserPmOrAboveOrHoSD: this.isUserPmOrAboveOrHoSD,
          isUserCM: this.isUserCM,
          loginUserEmail: this.loginUserEmail,
        };
      },
      isSortable: true,
    },
    {
      name: 'lastUpdated',
      displayName: 'Last Updated',
      type: TableColumnType.component,
      component: SdHistoryColumnComponent,
      getComponentInput: (row: ISdVoteDto) => {
        return {
          vote: row,
          isUserPmOrAboveOrHoSD: this.isUserPmOrAboveOrHoSD,
          afterSaveSubject: this.afterSaveVoteSubject,
         };
      },
      isSortable: true,
    },
  ];

  updateTableColumns() {
    if (this.isUserCM) {
      this.viewVoteTableColumns.forEach((column) => {
        if (column.name === 'checkbox') {
          column.isHidden = this.isUserCM; // Hide checkbox column if isUserCM is true
        }
      });
    }
  }

  ngOnInit() {
    this.getBallotVotes(this.sdBallotDetail?.projectId??0);
  }


  getBallotVotes(projectId: number): void {
    this._sdBallotService.getVotes(projectId).subscribe(
      response => {
        this.ballotVotes = response?.content;
        this.initVotes();
      },
      error => {
        console.error('Error updating ballot details:', error);
      }
    );
  }

  initVotes() {
    if (this.ballotVotes) {
      this.allVotes = this.ballotVotes?.map((vote) => ({
        ...vote,
        votingRights: vote.isEligible ? this.eligible : this.notEligible,
      }));
      this.showingVotes = this.allVotes.length > 50
      ? this.allVotes.slice(0, 50)
      : this.allVotes;
      this.totalRecords = this.allVotes.length;
      this.isJoint = this.sdBallotDetail?.isJoint??false;
    }
    this.evaluateLocationDropdownVisibility();
  }

  onFilterChange(value: any, fromVotingRight: boolean) {
    if (fromVotingRight) {
      this.votingRightDropdown = value;

      if (this.locationDropdown) {
        // Apply both filters if locationDropdown is not null or empty
        const filteredByVotingRight = this.filterByVotingRight(value);
        this.showingVotes = this.filterByLocation(
          this.locationDropdown,
          filteredByVotingRight
        );
      } else {
        // Apply only voting rights filter
        this.showingVotes = this.filterByVotingRight(value);
      }
    } else {
      this.locationDropdown = value;

      if (this.votingRightDropdown) {
        // Apply both filters if votingRightDropdown is not null or empty
        const filteredByLocation = this.filterByLocation(value);
        this.showingVotes = this.filterByVotingRight(
          this.votingRightDropdown,
          filteredByLocation
        );
      } else {
        // Apply only location filter
        this.showingVotes = this.filterByLocation(value);
      }
    }
    this.isDropdownChanged = true;
    this.onRowsUpdated(this.showingVotes);
  }

  public filterByVotingRight(
    value: string,
    votes: ISdVoteDto[] = this.allVotes
  ): ISdVoteDto[] {
    switch (value) {
      case 'eligible':
        return votes.filter((vote) => vote.isEligible);
      case 'notEligible':
        return votes.filter((vote) => !vote.isEligible);
      default:
        return votes;
    }
  }

  public filterByLocation(
    value: string,
    votes: ISdVoteDto[] = this.allVotes
  ): ISdVoteDto[] {
    switch (value) {
      case 'aus-nz':
        return votes.filter(
          (vote) =>
            Country.Australia.includes(vote.country?.toLowerCase()) ||
            Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      case 'au':
        return votes.filter((vote) =>
          Country.Australia.includes(vote.country?.toLowerCase())
        );
      case 'nz':
        return votes.filter((vote) =>
          Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      default:
        return votes;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  evaluateLocationDropdownVisibility() {
    const hasNZCommitteeVotes = this.allVotes.find(
      (vote) =>
        vote.country && Country.NewZealand.includes(vote.country.toLowerCase())
    );
    this.isShowingLocations = hasNZCommitteeVotes != null && this.isJoint;
  }

  onRowsUpdated(updatedRows: ISdVoteDto[]): void {
    // selected votes for proxy vote
    this.showingVotes = updatedRows;
  }

  onSelectedRowsChanged(updatedRows: ISdVoteDto[]): void {
    // selected votes for proxy vote
    this.selectedVotes = updatedRows;
    this.selectedVoteCount = this.selectedVotes.length;
  }


  onPageChange(page: number): void {
    this.selectedPageNumber = page;
    const startIndex = (page - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.allVotes.length);
    const paginatedVotes = this.allVotes.slice(startIndex, endIndex);
    this.showingVotes = paginatedVotes;
  }

}
